import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Placeholder, Popover, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import DstTable from "../../../../components/DstTable/DstTable";
import DstTableSearch from "../../../../components/DstTable/DstTableSearch/DstTableSearch";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { completeNavigate } from "../../../../services/UtilityService";
import { programModule, programSprintObject, programUser } from "./CohortProgramManager.d";
import { fetchProgramManagementSprints, resolveModuleStatus } from "./CohortProgramManager.function";

const CohortProgramManager = ({
  cohortId,
  language,
  onCountLoaded,
}: {
  cohortId: string;
  language: string;
  onCountLoaded?: (count: number) => void;
}) => {
  const navigate = useNavigate();

  const [sprints, setSprints] = useState<programSprintObject[]>([]);
  const [selectedSprint, setSelectedSprint] = useState<number | null>(null);
  const [loadingSprints, setLoadingSprints] = useState(true);
  const [filteredData, setFilteredData] = useState<programUser[] | null>(null);

  useEffect(() => {
    fetchProgramManagementSprints(
      cohortId,
      language,
      (sprintsData: programSprintObject[]) => {
        setSprints(sprintsData);
        onCountLoaded?.(sprintsData.length);

        if (sprintsData.length > 0) {
          const currentSprint = sprintsData.find((data) => !!data.current_sprint);
          setSelectedSprint(currentSprint ? currentSprint.sprint.id : sprintsData[0].sprint.id);
        }
      },
      setLoadingSprints
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, language]);

  useEffect(() => {
    setFilteredData(null);
  }, [selectedSprint]);

  const selectedSprintData = useMemo(
    () => sprints.find((sprintData) => sprintData.sprint.id === selectedSprint) ?? null,
    [sprints, selectedSprint]
  );

  const moduleColumns = useMemo((): programModule[] => {
    if (!selectedSprintData) return [];

    return Array.from(
      new Map(
        selectedSprintData.users
          .flatMap((user) => user.modules || [])
          .sort((moduleA, moduleB) => moduleA.order - moduleB.order)
          .map((module) => [module.module_id, module])
      ).values()
    );
  }, [selectedSprintData]);

  const renderModuleStatus = useCallback(
    (module?: programModule) => {
      const status = resolveModuleStatus(module, language);
      const iconColor =
        status.variant === "success"
          ? "var(--color-success-green)"
          : status.variant === "danger"
          ? "var(--color-red-bright)"
          : status.variant === "warning"
          ? "var(--color-yellow)"
          : "var(--color-black)";

      return (
        <span>
          {status.icon && <span className={`${status.icon} me-2`} style={{ color: iconColor }} />}
          {status.label}
        </span>
      );
    },
    [language]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.TABLE_HEADER.LAST_NAME"),
        key: "last_name",
      },
      {
        name: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.TABLE_HEADER.FIRST_NAME"),
        key: "first_name",
      },
      {
        name: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.TABLE_HEADER.BADGES"),
        key: "badges",
        sortCompute: (user: programUser) => user.flags?.length || 0,
        render: (user: programUser) => {
          const allFlags = user.flags || [];

          if (allFlags.length === 0) return "N/A";

          const visibleFlags = allFlags.slice(0, 3);
          const remainingFlags = allFlags.slice(3);

          const popover = (
            <Popover id={`popover-flags-${user.id}`}>
              <Popover.Body>
                {remainingFlags.map((flag) => (
                  <div key={`flag-${flag.id}`} className="d-flex align-items-center">
                    <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
                    <span>{flag.name}</span>
                  </div>
                ))}
              </Popover.Body>
            </Popover>
          );

          return (
            <span>
              {visibleFlags.map((flag) => (
                <OverlayTrigger
                  key={`flag-${flag.id}`}
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={<Tooltip id={`tooltip-${flag.id}`}>{flag.name}</Tooltip>}
                >
                  <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
                </OverlayTrigger>
              ))}

              {remainingFlags.length > 0 && (
                <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={popover}>
                  <span className="rounded light-blue-background blue-font p-1">+{remainingFlags.length}</span>
                </OverlayTrigger>
              )}
            </span>
          );
        },
      },
      {
        name: translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.TABLE_HEADER.STATUS"),
        key: "status",
        sortCompute: (user: programUser) => (user.is_late ? 1 : 0),
        render: (user: programUser) => {
          return (
            <span
              className="px-2 py-1 rounded-2"
              style={{
                backgroundColor: user.is_late ? "var(--bs-danger-bg-subtle)" : "var(--color-medium-green)",
                color: "var(--color-black)",
              }}
            >
              {translate(
                language,
                user.is_late
                  ? "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.LATE"
                  : "PAGES.COHORT.PROGRAM_MANAGEMENT.STATUS.ON_TIME"
              )}
            </span>
          );
        },
      },
    ];

    const moduleColumnsConfig = moduleColumns.map((moduleColumn) => ({
      name: (
        <React.Fragment>
          {moduleColumn.module_name}
          {!moduleColumn.is_mandatory && (
            <OverlayTrigger
              key={`tooltip-module-${moduleColumn.module_id}`}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-module-${moduleColumn.module_id}`}>
                  {translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.OPTIONAL")}
                </Tooltip>
              }
            >
              <span className="dst-icon-file-plus-02 ms-1 align-text-bottom" />
            </OverlayTrigger>
          )}
        </React.Fragment>
      ),
      key: `module_${moduleColumn.module_id}`,
      sortCompute: (user: programUser) => {
        const userModule = user.modules.find((module) => module.module_id === moduleColumn.module_id);
        return resolveModuleStatus(userModule, language).sortValue;
      },
      render: (user: programUser) => {
        const userModule = user.modules.find((module) => module.module_id === moduleColumn.module_id);
        return renderModuleStatus(userModule);
      },
      stylishTitle: () => (moduleColumn.is_mandatory ? {} : { color: "dimgray" }),
    }));

    return [...baseColumns, ...moduleColumnsConfig];
  }, [language, moduleColumns, renderModuleStatus]);

  if (loadingSprints) {
    return (
      <Placeholder animation="glow">
        <div className="mx-3">
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex">
              <Placeholder className="me-3 align-content-center mb-0" as="h2" xs={12} md={6} />
              <Placeholder as="input" xs={12} md={4} />
            </div>
            <DstButton mock btnWidth="100px" />
          </div>
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
        </div>
      </Placeholder>
    );
  }

  return (
    <div className="mx-3">
      <div className="d-flex justify-content-between mb-3">
        <div className="d-flex align-items-center flex-wrap">
          <h2 className="me-3 align-content-center mb-0">
            {translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.TITLE")}
          </h2>

          <DstTableSearch
            key={`search-${selectedSprint ?? "none"}`}
            data={selectedSprintData?.users || []}
            searchableColumns={["last_name", "first_name", "email"]}
            onSearch={setFilteredData}
          />
        </div>

        <div className="d-flex flex-column align-items-end gap-1">
          {sprints.length && (
            <select
              className="form-select"
              value={selectedSprint?.toString() || ""}
              onChange={(event) => setSelectedSprint(parseInt(event.target.value, 10))}
            >
              {sprints.map((sprintData) => (
                <option key={`sprint-option-${sprintData.sprint.id}`} value={sprintData.sprint.id}>
                  {sprintData.sprint.name}
                </option>
              ))}
            </select>
          )}
          {selectedSprintData && (
            <div className="d-flex align-items-center gap-2 xs-regular text-muted">
              <span>
                {formatDateHumanly(language, selectedSprintData.sprint.dt_start, true)}
                {" - "}
                {formatDateHumanly(language, selectedSprintData.sprint.dt_end, true)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="cohort-program-manager-table-container">
        {selectedSprintData ? (
          <React.Fragment>
            <DstTable
              data={filteredData ?? selectedSprintData?.users ?? []}
              columns={columns}
              onRowClick={(event, item) => completeNavigate(event, navigate, `/user/${item.id}`)}
              stickyFirstColumnsCount={2}
            />
          </React.Fragment>
        ) : (
          <div className="mt-3 text-muted">
            {translate(language, "PAGES.COHORT.PROGRAM_MANAGEMENT.NO_SPRINTS_FOUND")}
          </div>
        )}
      </div>
    </div>
  );
};

export default CohortProgramManager;
